<template>
  <div>
    <div class="files-for-tagging">
      <div
        class="bg-grey-lightest p-2 flex justify-between w-full sm:flex-row flex-col"
      >
        <!-- Toolbar in the Documents page style: one button size, pill chips in the Filters panel,
             Actions menu rows with a selected-count badge -->
        <div class="flex-1 my-1 sm:my-0 relative">
          <button
            class="border border-brand bg-white text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white"
            @click.prevent="
              show_filters = !show_filters;
              show_actions = false;
            "
            :class="{
              'bg-brand text-white': show_filters,
              'text-brand': !show_filters,
            }"
            v-is-authorized="{
              roles: [role],
              permissions: ['can_access_all_files'],
            }"
          >
            <i class="fa fa-filter"></i> Filters
          </button>
          <button
            class="border border-brand bg-white text-sm font-medium py-2 px-4 rounded ml-2 hover:bg-brand hover:text-white"
            @click.prevent="
              show_filters = false;
              show_actions = !show_actions;
            "
            :class="{
              'bg-brand text-white': show_actions,
              'text-brand': !show_actions,
            }"
            v-is-authorized="{ roles: [role], permissions: ['can_upload'] }"
          >
            <i class="fa fa-cog"></i> Actions
          </button>

          <div
            class="bg-white absolute py-4 px-4 shadow-lg z-20 rounded"
            v-if="show_filters"
          >
            <p class="text-xxs uppercase tracking-wide text-grey-dark font-semibold mb-2">Show</p>
            <div class="flex">
              <button
                type="button"
                class="border rounded-full py-2 px-3 mr-2 text-sm font-medium whitespace-no-wrap hover:bg-brand hover:text-white hover:border-brand"
                :class="mine_only == 1 ? 'bg-brand text-white border-brand' : 'border-grey text-grey-darker'"
                @click.prevent="showFilesFor('you')"
              >
                Uploaded by me
              </button>
              <button
                type="button"
                class="border rounded-full py-2 px-3 text-sm font-medium whitespace-no-wrap hover:bg-brand hover:text-white hover:border-brand"
                :class="mine_only == 0 ? 'bg-brand text-white border-brand' : 'border-grey text-grey-darker'"
                @click.prevent="showFilesFor('all')"
              >
                Everyone
              </button>
            </div>

            <p class="text-xxs uppercase tracking-wide text-grey-dark font-semibold mt-4 mb-2">Filter</p>
            <div class="flex">
              <button
                type="button"
                class="border rounded-full py-2 px-3 text-sm font-medium whitespace-no-wrap hover:bg-brand hover:text-white hover:border-brand"
                :class="showDuplicate ? 'bg-brand text-white border-brand' : 'border-grey text-grey-darker'"
                @click.prevent="showDuplicate = !showDuplicate"
              >
                Possible Duplicate
              </button>
            </div>
          </div>

          <div
            class="bg-white absolute w-64 py-4 px-4 shadow-lg z-20 rounded"
            v-if="show_actions"
          >
            <button
              type="button"
              class="group border p-2 rounded border-brand text-brand w-full flex items-center justify-between text-sm"
              @click.prevent="$refs.transfer_file_modal.open()"
              :class="{
                'hover:bg-brand hover:text-white': selected_files.length > 0,
                'text-grey cursor-not-allowed opacity-50':
                  selected_files.length == 0,
              }"
              :disabled="selected_files.length == 0"
            >
              <span><i class="fa fa-exchange-alt mr-1"></i> Transfer</span>
              <span
                class="ml-2 px-2 py-px rounded-full text-xs font-semibold"
                :class="selected_files.length > 0 ? 'bg-brand text-white group-hover:bg-white group-hover:text-brand' : 'bg-grey-light text-grey-darkest'"
              >{{ selected_files.length }}</span>
            </button>
          </div>
        </div>
        <div class="flex-1 sm:flex-none my-1 sm:my-0">
          <button
            type="button"
            class="border border-brand bg-white text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white"
            @click.prevent="selectAll"
            :class="{
              'text-brand': !selected_all,
              'bg-brand text-white': selected_all,
              'cursor-not-allowed opacity-50': selected_all,
            }"
            :disabled="selected_all"
          >
            <i class="fa fa-check-square" v-if="selected_all"></i>
            <i class="fa fa-square" v-else></i>
            Select all
          </button>
          <button
            type="button"
            class="border border-brand bg-white text-sm font-medium py-2 px-4 rounded ml-2 hover:bg-brand hover:text-white"
            @click.prevent="deselectAll"
            :class="{
              'text-brand': has_selected,
              'cursor-not-allowed opacity-50': !has_selected,
            }"
            :disabled="!has_selected"
          >
            <i class="fa fa-times"></i> Deselect all
          </button>
        </div>
      </div>
      <div v-if="data_fetching == false" class="overflow-auto">
        <div v-if="get_results.total > 0" class="flex flex-row">
          <file
            v-for="file in files_for_tagging"
            :key="file.id"
            :file="file"
            :selected-files="selected_files"
            :checkbox="true"
            :is-first="isFirst(file)"
            :is-last="isLast(file)"
            @select-checkbox="fileSelected"
            @deselect-checkbox="fileDeselected"
            @select="fileSelectedSingle"
            @deselect="fileDeselected"
            @refresh-files="getFilesForTagging"
            v-observe-visibility="
              (isVisible, entry) => visibilityChanged(isVisible, entry, file)
            "
          ></file>

          <div class="my-4 mx-2">
            <div
              v-if="load_more_results"
              class="w-32 h-full flex justify-center items-center flex-col bg-grey-lighter text-grey"
              v-observe-visibility="getNextPage"
            >
              <i class="fa fa-spin fa-circle-notch fa-4x"></i>
              loading...
            </div>
          </div>
        </div>

        <div
          v-else
          class="w-full h-64 bg-grey-light flex flex justify-center items-center"
        >
          <span>No Files available for Tagging.</span>
        </div>
      </div>

      <div
        v-else
        class="w-full h-64 bg-grey-light flex flex justify-center items-center"
      >
        <span>
          <i class="fa fa-circle-notch fa-spin"></i> Loading files. Please wait.
        </span>
      </div>
    </div>
    <form @submit.prevent="finishTagging">
      <div class="tagging-control">
        <!-- Datestamp - Description -  -->
        <!-- Labelled fields in the Vault Details style: label, grey/red hint, input underneath -->
        <mq-layout mq="sm_desktop+" class="flex flex-row border-t border-b p-4">
          <div class="datestamp w-1/3 pr-4">
            <label class="font-medium" for="filing_datestamp">
              Date Stamp
              <span class="text-xs text-red font-normal ml-1">Required</span>
            </label>
            <div class="w-full flex flex-row mt-1">
              <flat-pickr
                class="appearance-none border rounded-l w-full py-2 px-3 text-black"
                :class="{ 'border-red': errors.has('filing_datestamp') }"
                placeholder="Select Date"
                :config="flatpickr_config"
                :events="[
                  'on-change',
                  'on-close',
                  'on-destroy',
                  'on-key-down',
                  'on-month-change',
                  'on-open',
                  'on-parse-config',
                  'on-ready',
                  'on-value-update',
                ]"
                v-validate
                data-vv-rules="required|date_format:DD/MM/YYYY"
                data-vv-value-path="filing_datestamp"
                data-vv-name="filing_datestamp"
                v-model="filing_datestamp"
                name="filing_datestamp"
                v-tooltip="{
                  content: errors.first('filing_datestamp'),
                  show: errors.has('filing_datestamp'),
                  trigger: 'manual',
                  placement: 'bottom',
                }"
              ></flat-pickr>
              <button
                type="button"
                class="text-white px-3 rounded-r"
                :class="{
                  'bg-red': errors.has('filing_datestamp'),
                  'bg-brand': !errors.has('filing_datestamp'),
                }"
                data-toggle
              >
                <i class="fa fa-calendar"></i>
              </button>
            </div>
          </div>
          <div class="description w-2/3 pl-4">
            <label class="font-medium" for="filing_description">
              Description
              <span class="text-xs text-grey-darker font-normal ml-1">Optional</span>
            </label>
            <input
              class="appearance-none border rounded w-full py-2 px-3 mt-1 text-black"
              id="filing_description"
              type="text"
              placeholder="Description"
              v-model="description"
            />
          </div>
        </mq-layout>

        <!-- Datestamp - Description - mobile -->
        <mq-layout
          :mq="['mobile', 'tablet']"
          class="flex border-t border-b flex-wrap p-4"
        >
          <div class="datestamp w-full py-2">
            <label class="font-medium">
              Date Stamp
              <span class="text-xs text-red font-normal ml-1">Required</span>
            </label>
            <div class="w-full flex flex-row mt-1">
              <flat-pickr
                class="appearance-none border rounded-l w-full py-2 px-3 text-black"
                :class="{ 'border-red': errors.has('filing_datestamp') }"
                placeholder="Select Date"
                :config="flatpickr_config"
                :events="[
                  'on-change',
                  'on-close',
                  'on-destroy',
                  'on-key-down',
                  'on-month-change',
                  'on-open',
                  'on-parse-config',
                  'on-ready',
                  'on-value-update',
                ]"
                v-validate
                data-vv-rules="required|date_format:DD/MM/YYYY"
                data-vv-value-path="filing_datestamp"
                data-vv-name="filing_datestamp"
                v-model="filing_datestamp"
                name="filing_datestamp"
                v-tooltip="{
                  content: errors.first('filing_datestamp'),
                  show: errors.has('filing_datestamp'),
                  trigger: 'manual',
                  placement: 'bottom',
                }"
              ></flat-pickr>
              <button
                type="button"
                class="text-white px-3 rounded-r"
                :class="{
                  'bg-red': errors.has('filing_datestamp'),
                  'bg-brand': !errors.has('filing_datestamp'),
                }"
                data-toggle
              >
                <i class="fa fa-calendar"></i>
              </button>
            </div>
          </div>

          <div class="description w-full py-2">
            <label class="font-medium" for="filing_description_mobile">
              Description
              <span class="text-xs text-grey-darker font-normal ml-1">Optional</span>
            </label>
            <textarea
              class="appearance-none border rounded w-full py-2 px-3 mt-1 text-black"
              id="filing_description_mobile"
              placeholder="Description"
              rows="3"
              v-model="description"
            ></textarea>
          </div>
        </mq-layout>

        <div class="category-tags p-0 md:p-4">
          <p class="font-medium text-lg text-center md:text-left pt-4 pb-2">
            Add Tags
          </p>

          <!-- laptop/desktop -->
          <mq-layout
            mq="sm_desktop+"
            class="tags flex flex-row text-center overflow-y-auto"
            v-if="categorized_tags && categorized_tags.length != 0"
          >
            <tags
              v-for="category in categorized_tags"
              :category="category"
              :key="category.id"
              class="category flex-1 px-1"
            >
              <tag
                v-for="tag in category.tags"
                :key="tag.id"
                class="px-2 h-12"
                :tag="tag"
                :selected-files="selected_files"
                used-for="file-and-tag"
                @tagged="tagFiles"
                @untagged="untagFiles"
                :truncate="true"
              ></tag>
            </tags>
          </mq-layout>

          <!-- mobile/tablet -->
          <mq-layout
            :mq="['mobile', 'tablet']"
            class="tags p-4 bg-grey-light"
            v-if="categorized_tags && categorized_tags.length != 0"
          >
            <tabs>
              <tab
                v-for="(category, index) in categorized_tags"
                :key="index"
                :name="category.name"
                :selected="index == 0 ? true : false"
              >
                <tag
                  v-for="tag in category.tags"
                  :key="tag.id"
                  class="py-3 px-2"
                  :tag="tag"
                  :selected-files="selected_files"
                  used-for="file-and-tag"
                  @tagged="tagFiles"
                  @untagged="untagFiles"
                  :truncate="true"
                ></tag>
              </tab>
            </tabs>
          </mq-layout>

          <div
            class="bg-orange-lightest text-center border-orange text-orange-dark p-4"
            role="alert"
            v-if="categorized_tags && categorized_tags.length == 0"
          >
            <p>
              No Tags assigned to your Role, please contact your Vault
              Controller
            </p>
          </div>
        </div>

        <!-- Count sentence on the left, actions on the right (same pattern as the Documents toolbar) -->
        <div class="border-t border-b flex flex-col md:flex-row md:items-center md:justify-between p-4">
          <p class="text-sm text-grey-darker py-2">{{ selectedCountSentence }}</p>
          <div class="flex gap-x-2 md:gap-x-4">
            <button
              type="button"
              v-is-authorized="{
                roles: [role],
                permissions: ['can_delete_document_before_filing'],
              }"
              @click.prevent="$refs.delete_file_confirm.open()"
              class="flex-1 md:flex-none justify-center border border-red bg-white px-4 py-2 text-red rounded text-base font-medium hover:text-white hover:bg-red-dark hover:border-red-dark"
              :class="{
                'cursor-not-allowed opacity-50 text-grey border-grey':
                  selected_files.length == 0 || deleting,
              }"
              :disabled="selected_files.length == 0 || deleting"
            >
              <span v-if="deleting" class="flex items-center text-left">
                <i class="fa fa-spinner fa-spin mr-2"></i> <span>Deleting</span>
              </span>
              <span v-else class="flex items-center text-left">
                <i class="fa fa-trash mr-2"></i> <span>Delete {{ selectedDocumentsLabel }}</span>
              </span>
            </button>

            <button
              type="submit"
              class="flex-1 md:flex-none bg-brand border border-brand px-4 py-2 text-white rounded text-base font-medium hover:bg-brand-dark hover:border-brand-dark"
              :class="{
                'cursor-not-allowed opacity-50 text-grey border-grey':
                  selected_files.length == 0 || filing,
              }"
              :disabled="selected_files.length == 0 || filing"
              v-is-authorized="{
                roles: [role],
                permissions: ['can_file_document'],
              }"
            >
              <span v-if="filing" class="flex-items-center text-left">
                <i class="fa fa-spinner fa-spin mr-2"></i> <span>Filing</span>
              </span>
              <span v-else class="flex items-center text-left">
                <i class="fa fa-folder mr-2"></i> <span>File {{ selectedDocumentsLabel }}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </form>
    <app-modals
      ref="app_modals"
      @navigate-files="navigateFile"
      :with-navigation="true"
    ></app-modals>
    <sweet-modal icon="warning" overlay-theme="dark" ref="delete_file_confirm">
      <div class="flex flex-col">
        <span v-if="selected_files.length > 1">
          Are you sure you want to delete these
          <strong>{{ selected_files.length }}</strong> files?
        </span>

        <span v-else>Are you sure you want to delete this document?</span>

        <span class="text-red text-xs font-medium"
          >This document will be deleted permanently</span
        >

        <div class="mt-6">
          <button
            class="border bg-brand p-4 w-32 text-white rounded font-medium"
            @click.prevent="deleteFile()"
          >
            Yes
          </button>
          <button
            class="border bg-grey-lighter p-4 w-32 text-black border-grey rounded font-medium"
            @click="$refs.delete_file_confirm.close()"
          >
            No
          </button>
        </div>
      </div>
    </sweet-modal>
    <sweet-modal
      overlay-theme="dark"
      ref="transfer_file_modal"
      title="Transfer File"
    >
      <div class="p-4">
        <div class="p-2 flex flex-wrap">
          <div
            class="w-1/2 relative p-2"
            v-for="vault in vaults"
            :key="vault.id"
          >
            <a
              href="#"
              class="text-grey-darkest no-underline bg-brand-lightest overflow-hidden"
              @click.prevent="
                selected_vault_for_transfer = vault;
                $refs.confirm_file_transfer.open();
              "
            >
              <div
                class="flex flex-col justify-center items-center align-center bg-brand-lightest p-6 hover:shadow"
              >
                <div class="bg-vault-icon"></div>
                <span class="text-lg font-medium">{{ vault.name }}</span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </sweet-modal>
    <sweet-modal
      icon="warning"
      overlay-theme="dark"
      ref="confirm_file_transfer"
    >
      <div class="flex flex-col">
        <span v-if="selected_files.length > 1">
          Are you sure you want to transfer these
          <strong>{{ selected_files.length }}</strong> files to Vault
          <strong>{{ selected_vault_for_transfer.name }}</strong
          >?
        </span>

        <span v-else>
          Are you sure you want to transfer this file to Vault
          <strong>{{ selected_vault_for_transfer.name }}</strong
          >?
        </span>

        <div class="mt-6">
          <button
            class="border bg-brand p-4 w-32 text-white rounded font-medium"
            @click.prevent="transferFiles()"
          >
            Yes
          </button>
          <button
            class="border bg-grey-lighter p-4 w-32 text-black border-grey rounded font-medium"
            @click="$refs.confirm_file_transfer.close()"
          >
            No
          </button>
        </div>
      </div>
    </sweet-modal>
    <sweet-modal
      ref="processing_request"
      overlay-theme="dark"
      hide-close-button
    >
      <div class="mt-4">
        <p class="text-6xl text-grey-dark">
          <i class="fa fa-spin fa-circle-notch"></i>
        </p>
        Please wait...
      </div>
    </sweet-modal>
  </div>
</template>

<script>
import { Carousel, Slide } from "vue-carousel";
import flatPickr from "vue-flatpickr-component";
import File from "../../components/Vault/File";
import { mapGetters } from "vuex";
import Tags from "../../components/Tagging/Tags";
import Tag from "../../components/Tagging/Tag";
import Tabs from "../../components/FileView/Tabs";
import Tab from "../../components/FileView/Tab";
import AppModals from "../../components/AppModals";
import moment from "moment";

export default {
  components: {
    Carousel,
    Slide,
    File,
    Tags,
    Tag,
    Tabs,
    Tab,
    flatPickr,
    AppModals,
  },
  data() {
    return {
      showDuplicate: false,
      files_for_tagging: [],
      unfiled_count: 0,
      selected_files: [],
      get_results: [],

      filing_datestamp: "",
      description: "",

      data_fetching: true,
      filing: false,
      deleting: false,

      flatpickr_config: {
        wrap: true,
        allowInput: true,
        dateFormat: "d/m/Y",
        defaultDate: new Date(),
        monthSelectorType: "static",
      },

      mine_only: 1,

      get_result_range: {
        visible_indexes: [],
        from: 0,
        to: 0,
      },
      load_more_results: false,
      page: 1,
      items_per_page: 20,

      show_filters: false,
      show_actions: false,
      selected_vault_for_transfer: "",
    };
  },

  mounted() {
    this.filing_datestamp = new Date();
    this.getFilesForTagging();

    this.checkShouldOpenFile();

    if (
      !_.isNil(this.$route.params.open_file) &&
      this.$route.params.open_file
    ) {
      axios
        .get(
          `api/vaults/${this.$route.params.vault_id}/files/${this.$route.params.file_id}`
        )
        .then((resp) => {
          _.delay(() => {
            if (!_.isEmpty(this.files_for_tagging)) {
              let file = _.find(this.files_for_tagging, [
                "id",
                this.$route.params.file_id,
              ]);
              if (!_.isEmpty(file)) {
                this.fileSelectedSingle(file);
              }
            }
          }, 1500);
          if (resp.data && resp.data.id) {
            this.$ebus.$emit("view-file", resp.data);
          }
        });
    }

    this.$ebus.$on("tagged-from-fileview", (payload) => {
      this.selected_files.forEach((file) => {
        if (file.id == payload.file.id) {
          if (_.findIndex(file.tags, ["id", payload.tag.id]) === -1) {
            file.tags.push(payload.tag);
          }
        }
      });
    });

    this.$ebus.$on("untagged-from-fileview", (payload) => {
      this.selected_files.forEach((file) => {
        if (file.id == payload.file.id) {
          file.tags.splice(_.findIndex(file.tags, ["id", payload.tag.id]), 1);
        }
      });
    });

    this.$ebus.$on("filed-from-fileview", (payload) => {
      _.unset(this.files_for_tagging, [payload.id]);
      this.selected_files.splice(
        _.findIndex(this.selected_files, ["id", payload.id]),
        1
      );

      this.$noty.success(`The document: ${payload.filename} has been filed`, {
        killer: true,
        timeout: 2000,
      });
    });

    this.$ebus.$on("deleted-from-fileview", (payload) => {
      this.getFilesForTagging(payload);
    });

    this.$ebus.$on("archive-from-file", (payload) => {
      this.getFilesForTagging(payload);
    });

    this.$ebus.$on("remove-as-selected", (payload) => {
      this.selected_files.splice(
        _.findIndex(this.selected_files, ["id", payload.id]),
        1
      );
    });

    this.$ebus.$on("404-file", (payload) => {
      _.unset(this.files_for_tagging, [payload.id]);
      this.selected_files.splice(
        _.findIndex(this.selected_files, ["id", payload.id]),
        1
      );
    });
  },

  methods: {
    checkShouldOpenFile() {
      if (this.$route.query.file) {
        axios
          .get(
            `api/vaults/${this.$route.params.vault_id}/files/${this.$route.query.file}`
          )
          .then((resp) => {
            if (resp.data && resp.data.id) {
              this.$ebus.$emit("view-file", resp.data);
            } else {
              this.$noty.warning("File not found");
            }
          });
      }
    },

    visibilityChanged(isVisible, entry, file) {
      let total_hits = file.total_hits;

      let index = _.findIndex(this.files_for_tagging, { id: file.id });

      if (isVisible) {
        // check if index is in array, to avoid duplicates
        if (!_.includes(this.get_result_range.visible_indexes, index)) {
          // add to array
          this.get_result_range.visible_indexes.push(index);
        }
      } else {
        // check if index is in array, to avoid duplicates
        if (_.includes(this.get_result_range.visible_indexes, index)) {
          // remove if in array
          _.remove(this.get_result_range.visible_indexes, (v_index) => {
            return v_index == index;
          });
        }
      }

      this.get_result_range.from =
        _.min(this.get_result_range.visible_indexes) + 1;
      this.get_result_range.to =
        _.max(this.get_result_range.visible_indexes) + 1;
    },

    isEmpty(file) {
      return _.isEmpty(file);
    },

    getNextPage(isVisible, entry) {
      if (isVisible) {
        this.page++;
        this.load_more_files = true;

        this.getFilesForTagging();
      }
    },

    showFilesFor(type) {
      this.mine_only = type == "you" ? 1 : 0;
      this.files_for_tagging = [];
      this.selected_files = [];
      this.page = 1;
      this.getFilesForTagging();
    },

    getFilesForTagging(prev_action) {
      if (
        !_.isEmpty(prev_action) &&
        (prev_action == "file-deleted" ||
          prev_action == "tag-success" ||
          prev_action == "file-transfer" ||
          prev_action == "file-archived" ||
          prev_action == "files-refresh")
      ) {
        this.page = 1;
      }

      axios
        .get(`/api/vaults/${this.$route.params.vault_id}/files/for-tagging`, {
          params: {
            show_duplicate: this.showDuplicate ? 1 : 0,
            mine_only: this.mine_only,
            items_per_page: this.items_per_page,
            page: this.page,
          },
        })
        .then(({ data }) => {
          // file id is now used as the object key on the file_and_tag data
          if (
            !_.isEmpty(prev_action) &&
            (prev_action == "file-deleted" ||
              prev_action == "file-transfer" ||
              prev_action == "file-archived")
          ) {
            //   if file is deleted
            this.files_for_tagging = _.keyBy(data.data, "id");
          } else if (!_.isEmpty(prev_action) && prev_action == "tag-success") {
            //   if tagging is success and get next data
            let new_files = _.keyBy(data.data, "id");
            _.each(new_files, (file) => {
              let file_exists = _.filter(this.files_for_tagging, (file_tag) => {
                if (!_.isNil(file_tag)) {
                  return file.id == file_tag.id;
                }
              });

              if (_.isEmpty(file_exists)) {
                let obj = { [file.id]: file };
                this.files_for_tagging = _.merge(this.files_for_tagging, obj);
              }
            });
          } else if (prev_action == "files-refresh") {
            this.files_for_tagging = _.keyBy(data.data, "id");
            _.each(this.files_for_tagging, (file) => {
              if (!_.isNil(_.findKey(this.selected_files, ["id", file.id]))) {
                // remove and replace from selected_files
                this.selected_files.splice(
                  _.findIndex(this.selected_files, ["id", file.id]),
                  1
                );
                this.selected_files.push(file);
              }
            });
          } else {
            //   merge result from existing array
            if (!_.isEmpty(this.files_for_tagging)) {
              let new_files = _.keyBy(data.data, "id");

              _.each(new_files, (file) => {
                let file_exists = _.filter(
                  this.files_for_tagging,
                  (file_tag) => {
                    if (!_.isNil(file_tag)) {
                      return file.id == file_tag.id;
                    }
                  }
                );

                if (_.isEmpty(file_exists)) {
                  let obj = { [file.id]: file };
                  this.files_for_tagging = _.merge(this.files_for_tagging, obj);
                }
              });
            } else {
              this.files_for_tagging = _.keyBy(data.data, "id");
            }
          }

          this.get_results = data;
          this.unfiled_count = data.total;

          if (
            _.size(this.files_for_tagging) == data.total ||
            data.data.length == 0 ||
            data.to == data.total
          ) {
            this.load_more_results = false;
          } else {
            this.load_more_results = true;
          }

          this.data_fetching = false;
          this.$emit("update-unfiled", this.unfiled_count);
        });
    },

    fileSelected(file) {
      if (_.isNil(_.findKey(this.selected_files, ["id", file.id]))) {
        this.selected_files.push(file);
      }
    },

    fileSelectedSingle(file) {
      if (_.findIndex(this.selected_files, ["id", file.id]) === -1) {
        this.selected_files.push(file);
        if (file.filing_datestamp) {
          this.setFilingDate(file);
        }
      }
    },

    setFilingDate(file) {
      if (file.filing_datestamp) {
        this.filing_datestamp = moment(file.filing_datestamp).format(
          "DD/MM/YYYY"
        );
      }
    },

    fileDeselected(file) {
      this.selected_files.splice(
        _.findIndex(this.selected_files, ["id", file.id]),
        1
      );
    },

    tagFiles(tag) {
      _.forEach(this.selected_files, (file) => {
        if (_.findIndex(file.tags, ["id", tag.id]) === -1) {
          axios
            .post(
              `/api/vaults/${this.$route.params.vault_id}/files/${file.id}/tags`,
              {
                tag_id: tag.id,
              }
            )
            .then(({ data }) => {
              this.$ebus.$emit("tag-success", tag);
              file.tags.push(tag);
            })
            .catch((error) => {
              if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                // console.log(error.response.data);
                // console.log(error.response.status);
                // console.log(error.response.headers);

                let data = error.response.data;
                if (!data.success) {
                  if (error.response.status == 404) {
                    this.$noty.error(
                      "Error on Tagging: File not found, removed from File and Tag!"
                    );
                    _.unset(this.files_for_tagging, [file.id]);
                    this.selected_files.splice(
                      _.findIndex(this.selected_files, ["id", file.id]),
                      1
                    );
                    this.$ebus.$emit("remove-tag-loading");
                  } else {
                    this.$noty.error(data.message);
                    this.$ebus.$emit("remove-tag-loading");
                    this.getFilesForTagging("files-refresh");
                  }
                }
              } else if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                // http.ClientRequest in node.js
                console.log(error.request);
              } else {
                // Something happened in setting up the request that triggered an Error
                console.log("Error", error.message);
              }
              console.log(error.config);
            });
        }
      });
    },

    untagFiles(tag) {
      _.forEach(this.selected_files, (file) => {
        axios
          .delete(
            `/api/vaults/${this.$route.params.vault_id}/files/${file.id}/tags`,
            {
              data: {
                tag_id: tag.id,
              },
            }
          )
          .then(({ data }) => {
            this.$ebus.$emit("untag-success", tag);
            file.tags.splice(_.findIndex(file.tags, ["id", tag.id]), 1);
          })
          .catch((error) => {
            if (error.response) {
              // The request was made and the server responded with a status code
              // that falls out of the range of 2xx
              //   console.log(error.response.data);
              //   console.log(error.response.status);
              //   console.log(error.response.headers);

              let data = error.response.data;
              if (!data.success) {
                if (error.response.status == 404) {
                  this.$noty.error(
                    "Error on Tagging: File not found, removed from File and Tag!"
                  );
                  _.unset(this.files_for_tagging, [file.id]);
                  this.selected_files.splice(
                    _.findIndex(this.selected_files, ["id", file.id]),
                    1
                  );
                  this.$ebus.$emit("remove-tag-loading");
                } else {
                  this.$noty.error(data.message);
                }
              }
            } else if (error.request) {
              // The request was made but no response was received
              // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
              // http.ClientRequest in node.js
              console.log(error.request);
            } else {
              // Something happened in setting up the request that triggered an Error
              console.log("Error", error.message);
            }
            console.log(error.config);
            this.$ebus.$emit("remove-tag-loading");
          });
      });
    },

    finishTagging() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.filing = true;
          let selected_files_length = _.size(this.selected_files);
          let patched_files = 0;
          _.forEach(this.selected_files, (file) => {
            axios
              .patch(
                `/api/vaults/${this.$route.params.vault_id}/files/${file.id}/mark-as-filed`,
                {
                  description: this.description,
                  filing_datestamp: moment(this.filing_datestamp, [
                    "DD/MM/YYYY",
                    "YYYY-MM-DD",
                  ]).format("YYYY-MM-DD"),
                  ignore_filing_date: true,
                }
              )
              .then(({ data }) => {
                patched_files++;

                _.unset(this.files_for_tagging, [data.id]);

                this.$noty.success(
                  `The document: ${data.filename} has been filed`,
                  {
                    timeout: 2000,
                  }
                );

                if (selected_files_length == patched_files) {
                  this.taggingSuccess();
                }
              })
              .catch(({ response }) => {
                if (response && response.data) {
                  let data = response.data;
                  this.filing = false;
                  this.updating = false;
                  this.errors.clear();
                  if (!data.success) {
                    if (response.status == 404) {
                      this.$noty.error(
                        "Error on Filing: File not found, removed from File and Tag!"
                      );
                      _.unset(this.files_for_tagging, [file.id]);
                      this.selected_files.splice(
                        _.findIndex(this.selected_files, ["id", file.id]),
                        1
                      );
                    } else {
                      this.$noty.error(data.message);
                    }
                  }
                }
              });
          });
        }
      });
    },

    taggingSuccess() {
      this.selected_files = [];

      // reset and refresh view
      this.description = "";
      this.filing_datestamp = new Date();
      this.filing = false;
      this.$ebus.$emit("update-unfiled");
      this.$ebus.$emit("refresh-vaults");
      this.errors.clear();

      this.getFilesForTagging("tag-success");
    },

    deleteFile() {
      this.deleting = true;
      this.$refs.delete_file_confirm.close();
      this.$refs.processing_request.open();
      let selected_files_length = _.size(this.selected_files);
      let soft_deleted_files = 0;
      _.forEach(this.selected_files, (file) => {
        axios
          .delete(`api/vaults/${file.vault_id}/files/${file.id}`)
          .then(({ data }) => {
            if (data) {
              soft_deleted_files++;

              if (selected_files_length == soft_deleted_files) {
                // this.deletePermanently();
                this.deleting = false;
                this.$ebus.$emit("refresh-vaults");
                this.selected_files = [];
                this.$ebus.$emit("update-unfiled");
                this.getFilesForTagging("file-deleted");
                this.$noty.success("Files has been deleted!", {
                  killer: true,
                  timeout: 1500,
                });
                this.$refs.processing_request.close();
              }
            }
          })
          .catch(({ response }) => {
            this.$refs.processing_request.close();
            this.deleting = false;
            if (response) {
              let data = response.data;
              if (!data.success) {
                if (response.status == 404) {
                  this.$noty.error(
                    "Error on Delete: File not found, removed from File and Tag!"
                  );
                  _.unset(this.files_for_tagging, [file.id]);
                  this.selected_files.splice(
                    _.findIndex(this.selected_files, ["id", file.id]),
                    1
                  );
                } else {
                  this.$noty.error(data.message);
                }
              }
            }
          });
      });
    },

    deletePermanently() {
      let selected_files_length = _.size(this.selected_files);
      let hard_deleted_files = 0;

      _.forEach(this.selected_files, (file) => {
        axios
          .delete(`api/vaults/${file.vault_id}/files/${file.id}`, {
            params: {
              permanent_delete: 1,
            },
          })
          .then(({ data }) => {
            if (data) {
              hard_deleted_files++;

              if (selected_files_length == hard_deleted_files) {
                this.deleting = false;
                this.$ebus.$emit("refresh-vaults");
                this.selected_files = [];
                this.$ebus.$emit("update-unfiled");
                this.getFilesForTagging("file-deleted");
                this.$noty.success("Files has been deleted!", {
                  killer: true,
                  timeout: 1500,
                });
                this.$refs.processing_request.close();
              }
            }
          })
          .catch(({ response }) => {
            this.$refs.processing_request.close();
            this.deleting = false;
            if (response) {
              let data = response.data;
              if (!data.success) {
                if (response.status == 404) {
                  this.$noty.error(
                    "Error on Delete: File not found, removed from File and Tag!"
                  );
                  _.unset(this.files_for_tagging, [file.id]);
                  this.selected_files.splice(
                    _.findIndex(this.selected_files, ["id", file.id]),
                    1
                  );
                } else {
                  this.$noty.error(data.message);
                }
              }
            }
          });
      });
    },

    selectAll() {
      this.selected_files = [];
      this.selected_files = _.toArray(this.files_for_tagging);
    },

    deselectAll() {
      this.selected_files = [];
    },

    navigateFile(payload) {
      let key = _.findKey(_.toArray(this.files_for_tagging), [
        "id",
        payload.file.id,
      ]);
      if (!_.isNil(key)) {
        let next_key =
          payload.direction == "next"
            ? _.toInteger(key) + 1
            : _.toInteger(key) - 1;

        if (!_.isNil(_.toArray(this.files_for_tagging)[next_key])) {
          this.fileDeselected(payload.file);

          let paginate = {
            prev: true,
            next: true,
          };
          let first = _.head(_.toArray(this.files_for_tagging));
          let last = _.last(_.toArray(this.files_for_tagging));

          if (first.id == _.toArray(this.files_for_tagging)[next_key].id) {
            paginate.prev = false;
          }

          if (last.id == _.toArray(this.files_for_tagging)[next_key].id) {
            paginate.next = false;
          }

          this.fileSelectedSingle(_.toArray(this.files_for_tagging)[next_key]);
          this.$ebus.$emit("open-next", {
            file: _.toArray(this.files_for_tagging)[next_key],
            paginate: paginate,
          });
        }
      }
    },

    isFirst(file) {
      let first = _.head(_.toArray(this.files_for_tagging));
      return first.id == file.id ? true : false;
    },
    isLast(file) {
      let last = _.last(_.toArray(this.files_for_tagging));
      return last.id == file.id ? true : false;
    },

    transferFiles() {
      this.$refs.confirm_file_transfer.close();
      this.$refs.processing_request.open();

      let count = 0;
      let selected_files_for_transfer_length = this.selected_files.length;

      _.each(this.selected_files, (file) => {
        axios
          .patch(
            `api/vaults/${this.$route.params.vault_id}/files/${file.id}/transfer-vault`,
            {
              vault_id: this.selected_vault_for_transfer.id,
            }
          )
          .then(({ data }) => {
            if (data.success) {
              this.selected_files.splice(
                _.findIndex(this.selected_files, ["id", file.id]),
                1
              );
            }

            count++;

            if (count == selected_files_for_transfer_length) {
              this.$refs.processing_request.close();
              this.$noty.success(
                "File/s has been transfered to " +
                  this.selected_vault_for_transfer.name
              );
              this.show_actions = false;
              this.getFilesForTagging("file-transfer");
              this.$refs.transfer_file_modal.close();
            }
          })
          .catch(({ response }) => {
            let data = response.data;
            this.$refs.processing_request.close();
            this.$refs.transfer_file_modal.close();
            if (!data.success) {
              if (response.status == 404) {
                this.$noty.error("Error: File not found");
              } else {
                this.$noty.error(data.message);
              }
            }
          });
      });
    },
  },

  computed: {
    ...mapGetters({
      categorized_tags: "getCategorizedTags",
      role_id: "getUserRoleId",
      role: "getUserRole",
      vaults: "getVaults",
    }),

    selectedCountSentence() {
      const n = this.selected_files.length;
      if (n === 0) return "No documents selected";
      return n === 1 ? "1 document selected" : `${n} documents selected`;
    },

    // Button labels: "Document" for none or one selected, "3 Documents" for several
    selectedDocumentsLabel() {
      const n = this.selected_files.length;
      return n > 1 ? `${n} Documents` : "Document";
    },

    has_selected() {
      if (!_.isEmpty(this.selected_files)) {
        if (_.size(this.selected_files) != _.size(this.files_for_tagging)) {
          return false;
        }
        return true;
      }

      return false;
    },

    selected_all() {
      if (_.size(this.selected_files) == _.size(this.files_for_tagging)) {
        return true;
      }

      return false;
    },
  },

  watch: {
    showDuplicate() {
      this.files_for_tagging = [];
      this.selected_files = [];
      this.page = 1;
      this.getFilesForTagging();
    },
    files_for_tagging() {
      if (
        !_.isNil(this.$route.params.open_file) &&
        this.$route.params.open_file
      ) {
        this.mine_only = this.$route.params.mine_only;
        this.getFilesForTagging();
        let file = _.find(this.files_for_tagging, [
          "id",
          this.$route.params.file_id,
        ]);
        if (!_.isEmpty(file)) {
          this.fileSelectedSingle(file);
        }

        delete this.$route.params.open_file;
        delete this.$route.params.file_id;
      }
    },
  },
};
</script>
